import { toast } from "react-toastify";

/**
 * Shares a URL via the OS share sheet on touch devices, and copies it to the
 * clipboard with a toast everywhere else.
 *
 * Gated on a coarse pointer rather than on navigator.share existing: desktop
 * browsers (notably Safari and Chrome on macOS) do support the API, but their
 * system share picker has no "Copy" option — and copying the link is the main
 * thing people want on desktop. iOS and Android share sheets include Copy.
 *
 * Deliberately never passes `text` to navigator.share: on iMessage/WhatsApp
 * it splits the message and suppresses the link's own rich preview, which
 * defeats the point of sharing a link at all.
 *
 * `copiedMessage` is only shown on the clipboard path — the native share
 * sheet has its own UI, so no toast is needed when it succeeds.
 */
export async function shareLink({ url, title, copiedMessage }) {
  const isTouchDevice = window.matchMedia?.("(pointer: coarse)").matches;

  if (isTouchDevice && navigator.share) {
    try {
      await navigator.share({ url, title });
      return;
    } catch (error) {
      // AbortError = the user dismissed the share sheet — not a failure,
      // and not something to fall back to copying for.
      if (error.name === "AbortError") return;
      // Any other failure (e.g. share unsupported for this data despite the
      // API existing) falls through to the clipboard fallback below.
    }
  }

  try {
    await navigator.clipboard.writeText(url);
    toast.success(copiedMessage);
  } catch {
    toast.error("Failed to copy the link. Please try again later");
  }
}
